import { memo } from 'react';
import { clsx } from 'clsx';
import { VStack, Stack, HStack, Text, TextBlock } from '@library';
import RenderComponents from '../../../content/components/RenderComponents';
import StoryblokRender from '../../../content/components/StoryblokRender';
import type {
    ComponentsHandler,
    FooterComponent,
} from '../../../content/types/types';
import ComponentsMapper from '../../../content/components/ComponentsMapper';
import useCurrentStore from '../../hooks/useCurrentStore';
import CompanyLogo from '../CompanyLogo';
import styles from './Footer.module.scss';
import FooterSections from './FooterSections';

const footerParagraphMapper = {
    ...ComponentsMapper,
    paragraph: ({ content }, customStyles) => {
        return (
            <div className={clsx(styles.paragraph, customStyles?.paragraph)}>
                <Text regular secondary>
                    <RenderComponents components={content} />
                </Text>
            </div>
        );
    },
} as ComponentsHandler;

const StoreInfo = memo(
    ({
        company_address,
        contact_details,
        email,
    }: Pick<
        FooterComponent,
        'company_address' | 'email' | 'contact_details'
    >) => {
        const { companyCode } = useCurrentStore();

        return (
            <VStack gap="small">
                <div>
                    <CompanyLogo dark className={styles.logo} />
                </div>

                <VStack gap="mini">
                    <div className={styles.address}>
                        <StoryblokRender
                            componentMapper={footerParagraphMapper}
                            components={company_address}
                            styles={{
                                paragraph: styles.addressLine,
                            }}
                        />
                    </div>
                    <TextBlock secondary>
                        <a
                            href={`mailto:${email}`}
                            className={clsx(styles.email, {
                                [styles.intercycle]: companyCode === 'ic',
                            })}
                        >
                            {email}
                        </a>
                    </TextBlock>

                    <StoryblokRender
                        componentMapper={footerParagraphMapper}
                        components={contact_details}
                        styles={{
                            paragraph: styles.addressLine,
                        }}
                    />
                </VStack>
            </VStack>
        );
    }
);

const Footer = ({
    body,
    contact_details,
    company_address,
    copyright,
    under_copyright_body,
    email,
    social_links,
}: FooterComponent) => {
    return (
        <footer className={styles.footer}>
            <VStack expanded>
                <div>
                    <div
                        className={clsx(
                            styles.primaryContent,
                            styles.padded,
                            styles.noPaddingOnMobile
                        )}
                    >
                        <Stack
                            direction="responsiveColumnTabletRow"
                            justify="spaceBetween"
                            gap="medium"
                            expanded
                            wrap
                        >
                            <div className={styles.storeInfo}>
                                <StoreInfo
                                    contact_details={contact_details}
                                    company_address={company_address}
                                    email={email}
                                />
                            </div>
                            <div>
                                <FooterSections sections={body} />
                            </div>
                        </Stack>
                    </div>
                    <div>
                        <hr className={styles.divider} />
                    </div>
                    <div
                        className={clsx(styles.secondaryContent, styles.padded)}
                    >
                        <Stack
                            direction={
                                social_links.length > 0
                                    ? 'responsiveColumnTabletRowReversed'
                                    : 'responsiveColumnTabletRow'
                            }
                            align="responsiveDefaultTabletCenter"
                            justify="spaceBetween"
                            gap="medium"
                        >
                            {social_links.length > 0 && (
                                <div className={styles.socialLinks}>
                                    <HStack
                                        align="center"
                                        justify="responsiveSpaceBetweenTabletDefault"
                                        gap="small"
                                    >
                                        <StoryblokRender
                                            components={social_links}
                                        />
                                    </HStack>
                                </div>
                            )}
                            <Stack
                                direction="responsiveColumnTabletRow"
                                align="responsiveDefaultTabletCenter"
                                gap="responsiveSmallTabletMedium"
                                expanded
                            >
                                <div className={styles.copyright}>
                                    <Text secondary regular>
                                        &copy; {copyright}
                                    </Text>
                                </div>
                                <StoryblokRender
                                    components={under_copyright_body}
                                    componentMapper={footerParagraphMapper}
                                    styles={{
                                        link: styles.footerLink,
                                        paragraph: styles.copyrightLinks,
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </div>
                </div>
            </VStack>
        </footer>
    );
};

export default memo(Footer);
